import { useState } from 'react'
import { Globe, Menu, X, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { WebsiteNav } from '../../types'

interface Props {
  logoUrl?: string
  companyName: string
  primaryColor: string
  nav: WebsiteNav[]
  activeSlug: string
  onNavClick: (slug?: string) => void
  slugMap: Record<string, string>
  lang: string
  onToggleLang: () => void
}

export default function PublicNavbar({ logoUrl, companyName, primaryColor, nav, activeSlug, onNavClick, slugMap, lang, onToggleLang }: Props) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-black/[0.04]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/my-website"
              className="p-1 rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all"
              title="返回工作台">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            {logoUrl ? (
              <img src={logoUrl} alt="" className="h-7 w-7 rounded-lg object-cover" />
            ) : (
              <div className="h-7 w-7 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: primaryColor }}>
                {companyName?.[0] || 'P'}
              </div>
            )}
            <span className="text-sm font-semibold text-[#1a1a1a]">{companyName}</span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {nav.sort((a, b) => a.navOrder - b.navOrder).map(item => {
              const itemSlug = item.pageId ? slugMap[item.pageId] : undefined
              const isActive = itemSlug && activeSlug === itemSlug
              return (
                <button key={item.id}
                  onClick={() => onNavClick(itemSlug)}
                  className={`px-3 py-1.5 text-[13px] rounded-lg transition-all ${
                    isActive ? 'font-medium' : 'text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04]'
                  }`}
                  style={isActive ? { color: primaryColor } : undefined}>
                  {lang === 'en' && item.labelEn ? item.labelEn : item.label}
                </button>
              )
            })}
          </nav>

          <div className="flex items-center gap-2">
            <button onClick={onToggleLang}
              className="flex items-center gap-1 px-2 py-1.5 text-[12px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all">
              <Globe className="h-3.5 w-3.5" />
              {lang === 'zh' ? 'EN' : '中'}
            </button>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-1.5 rounded-lg text-gray-400 hover:text-[#1a1a1a]">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="md:hidden py-2 border-t border-black/[0.04]">
            {nav.sort((a, b) => a.navOrder - b.navOrder).map(item => {
              const itemSlug = item.pageId ? slugMap[item.pageId] : undefined
              return (
                <button key={item.id}
                  onClick={() => { onNavClick(itemSlug); setMobileOpen(false) }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:text-[#1a1a1a] hover:bg-black/[0.03] rounded-lg">
                  {lang === 'en' && item.labelEn ? item.labelEn : item.label}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </header>
  )
}
